/**
 * MER-576: pure resolution logic for benchmark flags on KPI tiles.
 *
 * Plain .mjs so the node:test suite imports it with no build step (the
 * priorWindow.mjs / tenantScope.mjs precedent). The React provider, the
 * Supabase fetch and the types live in flagMap.ts / flagMap.d.mts.
 *
 * Context: BENCHMARK + FLAG have been computed every refresh since v4.3 and
 * synced to Supabase, but the dashboard's only consumer was flagStream.ts,
 * which counts RED rows for a toast. No tile ever read one, so every value in
 * the product rendered in var(--text-primary) with no icon -- which is what
 * "the palette is too dark / needs colour across the board" actually was.
 */

const LEVELS = new Set(["GREEN", "YELLOW", "RED"]);

/**
 * MER-716: the benchmark sources allowed to carry a pass/fail judgment.
 *
 * Every production benchmark used to be `historical`: percentiles of our own
 * past output. A number that is wrong but stable sits inside its own history
 * and reads green. So only a source independent of our output (the customer's
 * own target, a cited industry reference) may colour a tile or raise a
 * "critical alert"; a historical flag reads as a trend against the median of
 * the customer's recent periods, with the source named. For those flags the
 * pipeline stores that median as benchmark_value (flags.py trend_baseline),
 * not the percentile band edge, so the caption's number is what it says.
 * Mirrors STANDARD_BENCHMARK_SOURCES and the labels in
 * src/meridian_pipeline/canonical/flags.py.
 */
export const STANDARD_BENCHMARK_SOURCES = Object.freeze(["manual", "industry"]);

const SOURCE_LABELS = Object.freeze({
  manual: "your target",
  industry: "industry reference",
  historical: "your recent median",
  peer_group: "your peer group",
});

// A flag whose benchmark row could not be read is not a standard either.
const UNKNOWN_SOURCE_LABEL = "a derived baseline";

/**
 * MER-814: the smallest benchmark sample a tile will show anything for.
 *
 * Mirrors MIN_SAMPLE_SIZE in src/meridian_pipeline/canonical/benchmark.py. A
 * compute guard drops a whole period from a KPI's series when it refuses one
 * of that period's values, and on the live DWS warehouse that took
 * minutes_per_stop down to 1 surviving period of 37 in one scope and
 * yards_per_haul to 0 of 37. A median over one or two periods is that period
 * compared against itself, and it reads as a confident trend either way.
 *
 * The pipeline refuses to publish below this floor, so in normal operation
 * nothing here is below it. This is the independent check: the surface that
 * shows the judgment does not take the producer's word for what the judgment
 * rests on. A row with no sample_size at all (a `manual` target, which is a
 * client figure and not a history, or a row written before the column existed)
 * is NOT gated: unknown is not the same as thin.
 */
export const MIN_BENCHMARK_SAMPLE = 8;

// Mirrors GATE_COPY in sanityGate.ts. Repeated rather than imported because
// this file is plain .mjs for node:test and that one is TypeScript.
const THIN_SAMPLE_CAPTION = "Benchmark not available - awaiting source data";

export function isStandardSource(source) {
  return typeof source === "string" && STANDARD_BENCHMARK_SOURCES.includes(source);
}

export function sourceLabel(source) {
  return typeof source === "string" && Object.prototype.hasOwnProperty.call(SOURCE_LABELS, source)
    ? SOURCE_LABELS[source]
    : UNKNOWN_SOURCE_LABEL;
}

/**
 * benchmark_id -> benchmark_source, from `benchmark` rows. FLAG carries no
 * source of its own; it joins to BENCHMARK on benchmark_id.
 */
export function buildSourceMap(rows) {
  const map = new Map();
  for (const row of rows || []) {
    if (row?.benchmark_id && typeof row.benchmark_source === "string") {
      map.set(String(row.benchmark_id), row.benchmark_source);
    }
  }
  return map;
}

/**
 * MER-814: benchmark_id -> sample_size, from the same `benchmark` rows.
 *
 * Only a finite non-negative number is recorded. A null, a missing column
 * (rows synced before the column existed) or a `manual` target row leaves the
 * id out of the map entirely, which reads as unknown, not as thin.
 */
export function buildSampleMap(rows) {
  const map = new Map();
  for (const row of rows || []) {
    const size = row?.sample_size;
    if (row?.benchmark_id && typeof size === "number" && Number.isFinite(size) && size >= 0) {
      map.set(String(row.benchmark_id), size);
    }
  }
  return map;
}

/**
 * Key on (kpi, equipment, division). Division belongs in the key rather than in
 * a filter so a division-scoped flag and its company-wide sibling coexist and
 * the lookup can fall back from one to the other.
 */
export function flagKey(kpiName, equipmentType, division) {
  return `${kpiName}|${equipmentType || "all"}|${division || ""}`;
}

/**
 * Collapse rows to one entry per key, keeping the most recent period.
 *
 * FLAG is regenerated every refresh but rows are retained across periods, so
 * without this the map surfaces whichever period PostgREST happened to return
 * last -- i.e. a stale verdict, shown with full confidence.
 *
 * `sources` is buildSourceMap's output. Without it every entry is non-standard,
 * which is the safe reading: no source, no judgment.
 */
export function buildFlagMap(rows, sources, samples) {
  const map = new Map();
  for (const row of rows || []) {
    const level = String(row?.flag_level || "").toUpperCase();
    if (!row?.kpi_name || !LEVELS.has(level)) continue;
    const key = flagKey(row.kpi_name, row.equipment_type, row.division);
    const existing = map.get(key);
    // Later period wins. A null period loses to any dated row but still
    // registers, so an undated flag beats no flag at all.
    if (existing && (row.period_start || "") <= (existing.periodStart || "")) continue;
    const source =
      sources && row.benchmark_id ? (sources.get(String(row.benchmark_id)) ?? null) : null;
    // MER-814: what the benchmark rests on. null means unknown, not thin.
    const sampleSize =
      samples && row.benchmark_id ? (samples.get(String(row.benchmark_id)) ?? null) : null;
    map.set(key, {
      level,
      source,
      sampleSize,
      standard: isStandardSource(source),
      kpiName: row.kpi_name,
      equipmentType: row.equipment_type || "all",
      division: row.division ?? null,
      benchmarkValue: typeof row.benchmark_value === "number" ? row.benchmark_value : null,
      deviationPct: typeof row.deviation_pct === "number" ? row.deviation_pct : null,
      periodStart: row.period_start ?? null,
    });
  }
  return map;
}

/**
 * Resolve a widget to its flag, most specific match first.
 *
 * A tile scoped to roll_off in Jackson reads the Jackson roll_off verdict when
 * one exists, then the company-wide roll_off verdict, then the all-equipment
 * verdict. Degrading beats showing nothing, but only downward: a narrower
 * verdict is never applied to a broader tile.
 */
export function lookupFlag(map, column, equipmentType, division) {
  if (!column || !map) return undefined;
  const candidates = [
    flagKey(column, equipmentType, division),
    flagKey(column, equipmentType, null),
    flagKey(column, "all", division),
    flagKey(column, "all", null),
  ];
  for (const key of candidates) {
    const hit = map.get(key);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * What a tile shows for a resolved flag (MER-716).
 *
 * A standard-sourced flag keeps its level and says what it is measured
 * against: "18% above your target 45". A historical one gets NO level, so no
 * in-band / out-of-band chip and no colour, and its caption reads as movement
 * against a named source: "Trend: 18% above your recent median 12.50".
 *
 * @param {object | undefined} entry  lookupFlag's result
 * @param {string | null} formattedBenchmark  benchmarkValue through formatValue
 * @returns {{ level: "GREEN" | "YELLOW" | "RED" | undefined, caption: string | null }}
 */
export function describeFlag(entry, formattedBenchmark) {
  if (!entry) return { level: undefined, caption: null };
  // MER-814: a benchmark built on too few surviving periods carries neither a
  // chip nor a trend. It says so instead of saying nothing, because a tile
  // with no caption is indistinguishable from a tile whose number is fine.
  if (typeof entry.sampleSize === "number" && entry.sampleSize < MIN_BENCHMARK_SAMPLE) {
    return { level: undefined, caption: THIN_SAMPLE_CAPTION };
  }
  const level = entry.standard === true ? entry.level : undefined;
  if (entry.benchmarkValue === null || entry.benchmarkValue === undefined) {
    return { level, caption: null };
  }
  const dev = entry.deviationPct;
  const hasDev = typeof dev === "number" && Number.isFinite(dev);
  const direction = !hasDev ? "vs" : dev >= 0 ? "above" : "below";
  const magnitude = !hasDev
    ? ""
    : `${Math.abs(dev) >= 10 ? Math.round(Math.abs(dev)) : Math.abs(dev).toFixed(1)}% `;
  const reference = formattedBenchmark ? ` ${formattedBenchmark}` : "";
  const phrase = `${magnitude}${direction} ${sourceLabel(entry.source)}${reference}`;
  return { level, caption: level === undefined ? `Trend: ${phrase}` : phrase };
}

/**
 * A filter value may be a bare string, a single-element array, or a multi-value
 * array after the LOB chip narrows a declared rollup list. Only an unambiguous
 * single value can scope a flag; anything else reads as company-wide, because a
 * verdict computed for one LOB must never be shown on a tile spanning several.
 *
 * MER-739: it is also, and far more often, a CLAUSE. Almost every widget
 * declares `equipment_type: {op: in, value: [...]}` rather than a bare string
 * or array, and the caller passes `widget.filter.equipment_type` straight in.
 * The bare-string and bare-array branches never matched one, so every tile
 * fell back to the company-wide 'all' benchmark key and a per-line benchmark
 * was never once found. Unwrap `in` and `eq` (the two ops that can name a
 * single value); every other op stays company-wide by returning null.
 */
export function soleValue(value) {
  if (typeof value === "string" && value) return value;
  if (Array.isArray(value)) {
    return value.length === 1 && typeof value[0] === "string" ? value[0] || null : null;
  }
  if (value && typeof value === "object" && "op" in value) {
    if (value.op === "eq" || value.op === "in") return soleValue(value.value);
    return null;
  }
  return null;
}
